import {AfterViewInit, Component, OnDestroy, OnInit, ViewChild}       from '@angular/core';
import {
  ApDynComponentComponent
}                                                                     from '../../ap-dyncomponent/ap-dyncomponent.component';
import {
  ApDynGridColumnConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {PpLogfilesGridConfig}                                         from '../configs/pp-logfiles-grid.config';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {map}                                                          from 'rxjs/operators';
import {IPpLogfileGridItem}                                           from '../types/pp-logfile.types';
import {
  PlantProtectionPlanBookStore
}                                                                     from '../../stores/plant-protection/plant-protection-planbook.store';
import {FieldStore}                                                   from '../../stores/farm/field.store';
import {
  CropTypeStore
}                                                                     from '../../stores/base-data/crop.types.store';
import {DriverStore}                                                  from '../../stores/docu/driver.store';
import {PpLogfileService}                                             from '../services/pp-logfile.service';
import {LoginStore}                                                   from '../../stores/login/login.store';
import {
  PlantProtectionProductStore
}                                                                     from '../../stores/base-data/plantprotection.product.store';
import {
  ApDynGridPagerConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config-base';
import {
  ApDynGridPagerConfig
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-config';
import IDatasetFieldMap = Data.Sensor.IDatasetFieldMap;
import {MapFactoryStyler}                                             from '../../map-factory/style';
import {
  CampaignYearStore
}                                                                     from '../../stores/login/campaignyear.store';
import IPlantProtectionPlanBook = Data.TaskManagement.IPlantProtectionPlanBook;
import {ObjectFactory}                                                from 'ts-tooling';

@Component({
  selector: 'plant-protection-logfiles',
  template: `
    <ap-dyncomponent [caption]="'Global__Logfiles'"
                     [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [items]="items$ | async"
                     [columns]="columns"
                     [pager]="pager"
                     [canSearch]="true">
    </ap-dyncomponent>
  `,
  styles: []
})
export class PpLogfilesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(ApDynComponentComponent) dynComponent: ApDynComponentComponent;
  public columns: ApDynGridColumnConfigBase[] = [];
  public items$ = new BehaviorSubject<IPpLogfileGridItem[]>([]);
  public loading$: Observable<boolean> = of(true);
  public pager: ApDynGridPagerConfigBase;
  private _subscriptions: Subscription[] = [];

  constructor(private store: PlantProtectionPlanBookStore,
              private cropStore: CropTypeStore,
              private driverStore: DriverStore,
              private fieldStore: FieldStore,
              private loginStore: LoginStore,
              private gridConfig: PpLogfilesGridConfig,
              private campaignYearStore: CampaignYearStore,
              private productStore: PlantProtectionProductStore,
              private service: PpLogfileService) {
  }

  public ngOnInit(): void {
    this.columns = this.gridConfig.getGridColumns(this._subscriptions);
    this.pager = new ApDynGridPagerConfig({});
  }

  public ngAfterViewInit(): void {
    this._getDataFromStore();
    this._subscriptions.push(this.dynComponent.SelectedItems$.pipe(
      map((items: IPpLogfileGridItem[]) =>
        items.map((item) =>
          item.FieldId as string).filter((v, i, a) => a.indexOf(v) === i)),
    ).subscribe((ids) => {
      this.fieldStore.changeSelectedField(ids);
    }));
    this._subscriptions.push(this.dynComponent.SelectedItems$.subscribe(selectedItems => {
      MapFactoryStyler.Instance.clearStyleCache();
      this.service.toggleLogPointsOnMap(selectedItems, this.loginStore.SelectedFarmId);
    }));
    // to have same behavior like in n-fertilization we clear selection after user actions (e.g. after accept, reject etc.)
    this._subscriptions.push(this.service.onClearSelection.subscribe(() =>
      this.dynComponent.dynGrid.gridPaging.setSelectedKeys([])));
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach((s) => s.unsubscribe());
  }

  private _getDataFromStore(): void {
    this.loading$ = this.store.Listen((s) => s.logfilesLoading);
    this._subscriptions.push(combineLatest([
      this.store.Listen((s) => s.logfiles),
      this.store.Listen((s) => s.data),
      this.fieldStore.Listen((s) => s.data),
    ]).pipe(
      map(([logfiles, planBooks]) => {
        return logfiles.map<IPpLogfileGridItem>((logfile) => {
          logfile = ObjectFactory.Copy(logfile);
          const field = this.fieldStore.getFieldByFieldGeomId(logfile.FieldGeomId);
          const fieldGeom = this.fieldStore.getFieldGeomById(logfile.FieldGeomId);
          const planning = logfile.Status !== -1
            ? planBooks.find((pb) => pb.Id === logfile.PlantProtectionPlanBook?.Id) ?? this.service.getPlanning(logfile)
            : undefined;
          if (logfile.PlantProtectionPlanBook == null) {
            logfile.PlantProtectionPlanBook = planning;
          }
          const status = this._getLogStatus(logfile);
          const headerValues = JSON.parse(logfile.DataSet.HeaderValuesString);
          const productList = headerValues?.Agents.map((v: any) => v.Name).join('\n');
          const result: IPpLogfileGridItem = {
            Id: logfile.Id as string,
            SourceItem: logfile,
            Status: status.Status,
            StatusClass: status.StatusClass,
            StatusDesc: status.StatusDesc,
            LogStatus: status.LogStatus,
            FieldName: field != null ? this.fieldStore.getFieldNameById(field?.Id) : 'Global__UnknownFieldName',
            FieldId: field?.Id,
            Date: new Date(logfile?.DataSet?.Created),
            Crop: this.cropStore.GetCropTypeName(logfile.CropType),
            FieldArea: fieldGeom?.AreaHa,
            FileName: logfile?.DataSet?.DataSetName,
            Ec: logfile.Ec,
            Water: logfile.NContentAvg,
            Products: productList,
            Planning: {
              SourceItem: planning,
              Date: planning?.ApplicationDate,
              Note: planning?.Summary?.Title,
              Driver: this.driverStore.getDriverById(planning?.ExportDriver)
            }
          };
          return result;
        });
      })
    ).subscribe(items => this.items$.next(items)));

    this._subscriptions.push(this.campaignYearStore.Listen(s => s.selectedYear).subscribe(() => setTimeout(() => {
      this._loadDataFromStore();
    })));
  }

  private _loadDataFromStore(): void {
    this.store.loadLogfiles();
    this.store.loadPlanBooks();
    this.productStore.loadPlantProtectionProducts(false, false);
  }

  private _getLogStatus(logfile: IDatasetFieldMap): {
    Status: number,
    StatusClass: string,
    StatusDesc: string,
    LogStatus: number
  } {
    const status = this.service.logBookingReady(logfile) ? 1 : !this.service.logFileValid(logfile) ? -1 : 0;
    const logStatus = this.service.logIsRejected(logfile) ? -1 : this.service.logBooked(logfile) ? 1 : 0;
    return {
      Status: status,
      StatusDesc: status === 1 ? 'Global__Logfile_Ok' : status === -1 ? 'Global__Logfile_Field_Geom_Error' : 'Global__Logfile_Action_Needed',
      StatusClass: status === 1 ? 'ap-status-green' : status === -1 ? 'ap-status-red' : 'ap-status-yellow',
      LogStatus: logStatus
    };
  }
}
